import React, { useMemo } from "react";
import { useOutletContext, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import NavbarScroll from "../../components/subComponents/NavbarScroll";
import { useItemCategoryInfinite } from "../../hooks/useItemCategoryInfinite";
import { ConnectCard1 } from "../../components/subComponents/ConnectCard1";
import ConnectCard2 from "../../components/subComponents/ConnectCard2";
import { saffronRout, saffronURL } from "../../Url";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) =>
  safeArray(arr).map((item, i) => ({ ...item, position: i }));

const Features = () => {
  const {
    selectedCountry,
    selectedCategory,
    selectedLanguage,
    selectedState,
    selectedCity,
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const categoryName = searchParams.get("category_name") || "";
    const sectionKey = searchParams.get("section"); // ✅ persistent


  const {
    item: features,
    ref,
    hasNextPage,
    isFetchingNextPage,
    isPending: featuresPending,
  } = useItemCategoryInfinite({
    key: "connect_features",
    endpoint: "features",        // ✅ correct endpoint
    listKey: "stories",          // API returns stories
    category_name: categoryName,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    selectedState,
    selectedCity,
    enabled: true,
    threshold: 0.7,
        featured: sectionKey === "features" ? 1 : undefined,

  });

  const hasFeatures = features?.length > 0;

  // 🔹 Menu
  const { data: menuData } = useQuery({
    queryKey: [
      "menuData_connect_slide",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    staleTime: 1000 * 60 * 10,
  });

  const navbaritems = useMemo(() => {
    return menuData?.data?.items?.find((item) => item.key === "feature");
  }, [menuData]);

  const getFeatures = useMemo(() => addPosition(features), [features]);

  // 🔹 Category change
  const handleCategorySelect = (child) => {
    const params = new URLSearchParams(searchParams);

    if (child?.code) params.set("category_name", child.code);
    else params.delete("category_name");

    setSearchParams(params);
  };

  // 🔹 Navigate to details page
  const handleArticleClick = (item) => {
    const params = new URLSearchParams(location.search);
    const next = new URLSearchParams();

    ["country", "state", "city", "language", "category"].forEach((k) => {
      const v = params.get(k);
      if (v) next.set(k, v);
    });

    next.set("section", "features");

    navigate(
      {
        pathname: `${saffronRout}/details/features/${item.slug}`,
        search: `?${next.toString()}`,
      },
      {
        state: {
          sectionKey: "features",
          position: item.position,
        },
      }
    );

    window.scrollTo(0, 0);
  };

  return (
    <>
      <NavbarScroll
        items={navbaritems}
        activeCode={categoryName}
        onSelect={handleCategorySelect}
      />

      <section className="py-3 px-1 md:px-5 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {getFeatures?.map((item) => (
            <ConnectCard1
              key={item.id}
              item={item}
              onArticleClick={() => handleArticleClick(item)}
               imgH=" h-[200px]"
              objimg="object-fill"
            />
          ))}
        </div>

        {featuresPending && (
          <p className="text-gray-500">Loading articles…</p>
        )}

        {!featuresPending && !hasFeatures && (
          <p className="text-gray-400 italic">
            No articles found in <span className="font-medium">'Features'</span>
          </p>
        )}

        {hasNextPage && <div ref={ref} className="h-10" />}

        {isFetchingNextPage && (
          <div className="text-center text-sm text-gray-500 py-4">
            Loading more...
          </div>
        )}
      </section>
    </>
  );
};

export default Features;